import React, { useState } from 'react';
import { Droplets, Sparkles, Palette, Volume2, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import PageLayout, { PageSection, PageCTA } from '../components/common/PageLayout';
import SEOHead from '../components/common/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';

// Water Screen Hero Video
const WATER_SCREEN_VIDEO = 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/yipf5ks7_Water_movie_01.mp4';

const WATER_SCREEN_GALLERY = [
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/ef9nynyq_VIDEO-2026-04-10-19-37-54.mp4', title: 'Show en Vivo', type: 'video' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/7f7v9oeq_water_movie_02.mp4', title: 'Proyección Espectacular', type: 'video' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/lc9lzn46_6F12EF87-F46F-4FFE-8533-A9B40475E226.png', title: 'Cotton Silk', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/0aeqa8md_751D6A44-4C45-41C2-9CC2-460CB22448CF.png', title: 'NexAir Show', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/51aunvpj_77D8F1F5-F142-4E10-ACE9-DA7343FE1DE5.png', title: 'Park Residences', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/twojddqd_IMG_1247.avif', title: 'Ave de Fuego', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/83vl4cac_A19EEFEF-D39C-4D93-9DD6-D0DD77F2074C.png', title: 'Paradise Resorts', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/qsz97onz_94F891CD-7FDF-4D0C-A27A-92E6AD27A26B.png', title: 'Tigre Espectacular', type: 'image' },
];

const benefitIcons = [Droplets, Sparkles, Palette, Volume2];
const benefitKeys = ['hdScreen', 'magicEffects', 'customContent', 'syncAudio'];
const applicationKeys = ['corporate', 'entertainment', 'tourism', 'weddings', 'advertising', 'architecture'];

const PantallaDeAguaPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { t, language } = useLanguage();
  const page = t('seoPages.waterScreen');

  const handleBackClick = (e) => {
    e.preventDefault();
    window.location.href = '/';
  };

  return (
    <PageLayout>
      <SEOHead 
        title={language === 'es' ? "Pantalla de Agua | Proyecciones Espectaculares | NexAir México" : "Water Screen | Spectacular Projections | NexAir Mexico"}
        description={language === 'es' ? "Pantallas de agua con proyecciones HD para eventos en México. Transforma cualquier espacio en un escenario mágico con cortinas de agua, luces y efectos especiales." : "Water screens with HD projections for events in Mexico. Transform any space into a magical stage with water curtains, lights and special effects."}
        keywords={language === 'es' ? "pantalla de agua mexico, water screen, proyeccion en agua, fuente con proyeccion, eventos con agua, show de agua" : "water screen mexico, water projection, fountain projection, water events, water show"}
        canonicalUrl="https://nexair.com.mx/pantalla-de-agua"
      />
      
      {/* Video Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            poster="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/twojddqd_IMG_1247.avif"
            className="w-full h-full object-cover"
          >
            <source src={WATER_SCREEN_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[#030810] via-[#030810]/50 to-[#030810]/20" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-24">
          <a 
            href="/"
            onClick={handleBackClick}
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            {t('seoCommon.backToHome')}
          </a>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              {page.title}
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 font-light mb-4">
            {page.subtitle}
          </p>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {page.description}
          </p>
        </div>
      </section>
      
      {/* Benefits */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
          {page.immersiveTitle} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{page.immersiveHighlight}</span> {page.immersiveSuffix}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefitKeys.map((key, index) => {
            const Icon = benefitIcons[index];
            return (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{page.benefits[key].title}</h3>
                <p className="text-gray-400">{page.benefits[key].desc}</p>
              </div>
            );
          })}
        </div>
      </PageSection>
      
      {/* How it Works */}
      <PageSection className="bg-[#050a15]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/lc9lzn46_6F12EF87-F46F-4FFE-8533-A9B40475E226.png"
              alt={page.whatIs}
              className="rounded-2xl border border-white/10 w-full"
              loading="lazy"
            />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {page.whatIs}
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              {page.whatIsDesc1}
            </p>
            <p className="text-gray-300 text-lg mb-6">
              {page.whatIsDesc2}
            </p>
            <div className="space-y-3">
              {page.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageSection>
      
      {/* Gallery */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
          {page.galleryTitle}
        </h2>
        <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">
          {page.galleryDesc}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {WATER_SCREEN_GALLERY.map((item, index) => (
            <div 
              key={index}
              className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all cursor-pointer relative group"
              onClick={() => setLightboxImage(item)}
            >
              {item.type === 'video' ? (
                <video 
                  src={item.url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <img 
                  src={item.url} 
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              )}
              {/* Video indicator */}
              {item.type === 'video' && (
                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                  </svg>
                  Video
                </div>
              )}
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Applications */}
      <PageSection className="bg-[#050a15]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
          {page.applicationsTitle}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {applicationKeys.map((key, index) => (
            <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-cyan-500/30 transition-all">
              <h3 className="text-xl font-bold text-white mb-2">{page.applications[key].title}</h3>
              <p className="text-gray-400">{page.applications[key].desc}</p>
            </div>
          ))}
        </div>
      </PageSection>
      
      <PageCTA 
        title={page.ctaTitle}
        subtitle={page.ctaSubtitle}
        whatsappMessage={page.ctaWhatsapp}
      />

      {/* Lightbox for images and videos */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10"
            onClick={() => setLightboxImage(null)}
          >
            <XCircle size={40} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {lightboxImage.type === 'video' ? (
              <video 
                src={lightboxImage.url}
                autoPlay
                controls
                playsInline
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            ) : (
              <img 
                src={lightboxImage.url} 
                alt={lightboxImage.title}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            )}
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-bold">{lightboxImage.title}</h3>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default PantallaDeAguaPage;
